"use client";

/**
 * "Add to calendar" pill buttons shown on the signup success view.
 * Returns null when the slot has no date.
 */

import { useMemo } from "react";
import type React from "react";
import { getCalendarOptions } from "@/lib/utils/calendar-links";

export interface CalendarSlotInfo {
  activityTitle: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  timeZone: string | null;
}

interface CalendarAddButtonsProps {
  slotInfo: CalendarSlotInfo;
}

/**
 * Renders the canonical calendar-provider list (Google / Outlook / .ics) for a
 * registered activity slot. Returns null when the slot has no date.
 *
 * @param props - Slot details used to build the calendar URLs.
 * @returns The pill-button row, or null.
 */
export function CalendarAddButtons({ slotInfo }: CalendarAddButtonsProps): React.ReactElement | null {
  const event = useMemo(
    () => ({
      title: slotInfo.activityTitle,
      date: slotInfo.date,
      startTime: slotInfo.startTime,
      endTime: slotInfo.endTime,
      location: slotInfo.location,
      timeZone: slotInfo.timeZone,
    }),
    [slotInfo]
  );
  const options = useMemo(() => getCalendarOptions(event), [event]);

  if (!slotInfo.date) return null;

  const itemClass =
    "inline-flex min-h-[44px] items-center rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-center text-sm font-semibold text-gray-500">Add to your calendar</p>
      <div className="flex flex-wrap justify-center gap-2">
        {options.map((opt) =>
          opt.kind === "link" ? (
            <a key={opt.key} href={opt.href} target="_blank" rel="noopener noreferrer" className={itemClass}>
              {opt.label}
            </a>
          ) : (
            <button key={opt.key} type="button" onClick={opt.onClick} className={itemClass}>
              {opt.label}
            </button>
          )
        )}
      </div>
    </div>
  );
}
